import React, { useState } from 'react';
import { MapPin, Users, Clock, Phone, Mail, Check, AlertCircle, Coffee, Utensils, Wifi, Projector, Car, ExternalLink, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KlubbhusetSection = ({ t }) => (
  <div className="space-y-8">
    {/* Hero Card */}
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="aspect-video relative">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80"
          alt={t('pg.Rentals.139c5b')}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h2 className="text-3xl font-bold text-white mb-2">{t('rentals.clubhouse', 'Klubbhuset')}</h2>
          <div className="flex items-center gap-2 text-white/80">
            <MapPin className="w-4 h-4 text-green-400" aria-hidden="true" />
            <span>{t('pg.Rentals.781400')}</span>
          </div>
        </div>
      </div>

      <p className="text-xs italic text-slate-500 dark:text-slate-400 px-6 pt-3" data-testid="klubbhuset-image-disclaimer">
        {t('pg.Rentals.2373e5')}
      </p>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{t('rentals.aboutVenue', 'Om lokalet')}</h3>
        <p className="text-slate-500 dark:text-slate-400 mb-4">
          {t('rentals.clubhouseDesc', 'Lokalet består av ett rom med maks kapasitet på')} <strong className="text-slate-900 dark:text-white">40 {t('rentals.people', 'personer')}</strong>. 
          {t('rentals.clubhouseEquipment', 'Det er utstyrt med bord og stoler til 40 personer, kjøleskap, kaffetrakter, mikrobølgeovn, komfyr og koketopp, oppvaskmaskin og vannkoker.')}
        </p>
        
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              <strong>{t('rentals.note', 'Merk')}:</strong> {t('rentals.noTableware', 'Det finnes ikke tallerkener, bestikk eller annet dekketøy i lokalet. Ved behov må dette medbringes (engangs anbefales).')}
            </p>
          </div>
        </div>

        {/* Amenities */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Users className="w-4 h-4 text-green-500" aria-hidden="true" />
            <span className="text-sm">{t('rentals.max40', 'Maks 40 personer')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Coffee className="w-4 h-4 text-green-500" />
            <span className="text-sm">{t('rentals.coffeeMaker', 'Kaffetrakter')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Utensils className="w-4 h-4 text-green-500" />
            <span className="text-sm">{t('rentals.kitchen', 'Kjøkken')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Clock className="w-4 h-4 text-green-500" />
            <span className="text-sm">09:00 - 22:30</span>
          </div>
        </div>

        {/* Good for */}
        <h4 className="text-lg font-bold text-slate-900 mb-3">{t('pg.Rentals.1d5944')}</h4>
        <ul className="space-y-2 mb-6">
          <li className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Check className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.160836')}
          </li>
          <li className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Check className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.9f98f1')}
          </li>
          <li className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Check className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.ea7fe8')}
          </li>
        </ul>

        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
          <p className="text-red-400 text-sm">
            <strong>{t('pg.Rentals.db556a')}</strong> {t('pg.Rentals.6c6874')}
          </p>
        </div>
      </div>
    </div>

    {/* Pricing Table */}
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-xl font-bold text-slate-900 mb-6">{t('pg.Rentals.a4981a')}</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="text-left py-3 text-slate-500 font-medium"></th>
              <th className="text-center py-3 text-slate-500 font-medium">{t('pg.Rentals.10b711')}</th>
              <th className="text-center py-3 text-slate-500 font-medium">{t('pg.Rentals.0bd279')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <td className="py-4 text-slate-900 font-medium">{t('pg.Rentals.656cf1')}</td>
              <td className="py-4 text-center">
                <span className="text-green-500 font-bold text-lg">1 500 kr</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.a46589')}</span>
              </td>
              <td className="py-4 text-center">
                <span className="text-green-500 font-bold text-lg">2 500 kr</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.a46589')}</span>
              </td>
            </tr>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <td className="py-4 text-slate-900 font-medium">{t('pg.Rentals.c1ced8')}</td>
              <td className="py-4 text-center">
                <span className="text-green-500 font-bold text-lg">1 000 kr</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.a46589')}</span>
              </td>
              <td className="py-4 text-center">
                <span className="text-green-500 font-bold text-lg">1 500 kr</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.a46589')}</span>
              </td>
            </tr>
            <tr>
              <td className="py-4 text-slate-900 font-medium">{t('pg.Rentals.eba461')}</td>
              <td className="py-4 text-center">
                <span className="text-green-400 font-bold text-lg">{t('pg.Rentals.890564')}</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.e613e6')}</span>
              </td>
              <td className="py-4 text-center">
                <span className="text-green-400 font-bold text-lg">{t('pg.Rentals.890564')}</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.e613e6')}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <p className="text-slate-400 text-sm mt-4 text-center">{t('pg.Rentals.04beb8')}</p>

      {/* Member conditions */}
      <div className="mt-6 bg-slate-100 rounded-lg p-4">
        <h4 className="text-slate-900 font-bold mb-2">{t('pg.Rentals.7d5fa5')}</h4>
        <ul className="space-y-1 text-slate-500 text-sm">
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            {t('pg.Rentals.ffe734')}
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            {t('pg.Rentals.085ac5')}
          </li>
        </ul>
      </div>

      {/* Deposit */}
      <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
        <p className="text-slate-600 dark:text-slate-300">
          <strong className="text-green-500">{t('pg.Rentals.50c201')}</strong> {t('pg.Rentals.753f2e')} <strong>2 000 kr</strong>{t('pg.Rentals.c67605')}
        </p>
      </div>
    </div>
  </div>
);

const SFOSection = ({ t }) => (
  <div className="space-y-8">
    {/* Hero Card */}
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="aspect-video relative">
        <img
          src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80"
          alt={t('pg.Rentals.5d6fd2')}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h2 className="text-3xl font-bold text-white mb-2">{t('pg.Rentals.6a2cae')}</h2>
          <div className="flex items-center gap-2 text-white/80">
            <MapPin className="w-4 h-4 text-green-400" />
            <span>{t('pg.Rentals.31f98f')}</span>
          </div>
        </div>
      </div>

      <p className="text-xs italic text-slate-500 dark:text-slate-400 px-6 pt-3" data-testid="sfo-image-disclaimer">
        {t('pg.Rentals.2373e5')}
      </p>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{t('pg.Rentals.8b1433')}</h3>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          {t('pg.Rentals.ef1ed3')}
        </p>

        {/* Good for */}
        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-3">{t('pg.Rentals.d9f521')}</h4>
        <div className="grid md:grid-cols-2 gap-2 mb-6">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Check className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.d44312')}
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Check className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.0718b4')}
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Check className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.f21a28')}
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Check className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.9cd112')}
          </div>
        </div>

        {/* Room details */}
        <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4 mb-6">
          <h4 className="text-slate-900 dark:text-white font-bold mb-3">{t('pg.Rentals.c76855')}</h4>
          <p className="text-slate-500 dark:text-slate-400 mb-4">{t('pg.Rentals.748d67')}</p>
          
          <h5 className="text-slate-900 dark:text-white font-medium mb-2">{t('pg.Rentals.0a2194')}</h5>
          <ul className="space-y-1 text-slate-500 dark:text-slate-400 text-sm mb-4">
            <li className="flex items-center gap-2">
              <Utensils className="w-4 h-4 text-green-500" />
              {t('pg.Rentals.3589cd')}
            </li>
            <li className="flex items-center gap-2">
              <Coffee className="w-4 h-4 text-green-500" />
              {t('pg.Rentals.6d02d6')}
            </li>
            <li className="flex items-center gap-2">
              <Users className="w-4 h-4 text-green-500" />
              {t('pg.Rentals.0f880c')}
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              {t('pg.Rentals.8d9c5b')}
            </li>
          </ul>

          <h5 className="text-slate-900 dark:text-white font-medium mb-2">{t('pg.Rentals.305c6a')}</h5>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
            {t('pg.Rentals.54c861')}
          </p>

          <h5 className="text-slate-900 dark:text-white font-medium mb-2">{t('pg.Rentals.1eb2ca')}</h5>
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
            <Wifi className="w-4 h-4 text-green-500" />
            {t('pg.Rentals.6b1457')}
          </div>
        </div>

        {/* Amenities grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Users className="w-4 h-4 text-green-500" />
            <span className="text-sm">{t('pg.Rentals.556afe')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Projector className="w-4 h-4 text-green-500" />
            <span className="text-sm">{t('pg.Rentals.7011bf')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Utensils className="w-4 h-4 text-green-500" />
            <span className="text-sm">{t('pg.Rentals.205a72')}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <Clock className="w-4 h-4 text-green-500" />
            <span className="text-sm">09:00 - 01:00</span>
          </div>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              <strong>{t('pg.Rentals.6cfcde')}</strong> {t('pg.Rentals.0df233')}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Pricing Tables */}
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">{t('pg.Rentals.e01cc1')}</h3>
      
      {/* Table 1: Orgs and Private */}
      <div className="overflow-x-auto mb-8">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="text-left py-3 text-slate-500 font-medium"></th>
              <th className="text-center py-3 text-slate-500 font-medium">{t('pg.Rentals.10b711')}</th>
              <th className="text-center py-3 text-slate-500 font-medium">{t('pg.Rentals.5b9738')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <td className="py-4 text-slate-900 dark:text-white font-medium">
                {t('pg.Rentals.df121a')}<br />
                <span className="text-slate-400 text-sm">{t('pg.Rentals.3697e1')}</span>
              </td>
              <td className="py-4 text-center">
                <div className="mb-2">
                  <span className="text-green-500 font-bold">5 000 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.8e77fc')}</span>
                </div>
                <div>
                  <span className="text-slate-600 dark:text-slate-300 font-bold">3 500 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.9f7619')}</span>
                </div>
              </td>
              <td className="py-4 text-center">
                <div className="mb-2">
                  <span className="text-green-500 font-bold">7 000 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.8e77fc')}</span>
                </div>
                <div>
                  <span className="text-slate-600 dark:text-slate-300 font-bold">5 500 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.9f7619')}</span>
                </div>
              </td>
            </tr>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <td className="py-4 text-slate-900 dark:text-white font-medium">
                {t('pg.Rentals.47f908')}<br />
                <span className="text-slate-400 text-sm">{t('pg.Rentals.36fb77')}</span>
              </td>
              <td className="py-4 text-center">
                <div className="mb-2">
                  <span className="text-green-500 font-bold">7 500 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.8e77fc')}</span>
                </div>
                <div>
                  <span className="text-slate-600 dark:text-slate-300 font-bold">5 500 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.9f7619')}</span>
                </div>
              </td>
              <td className="py-4 text-center">
                <div className="mb-2">
                  <span className="text-green-500 font-bold">7 500 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.8e77fc')}</span>
                </div>
                <div>
                  <span className="text-slate-600 dark:text-slate-300 font-bold">5 500 kr</span>
                  <span className="text-slate-400 text-sm block">{t('pg.Rentals.9f7619')}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Table 2: Businesses */}
      <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4">{t('pg.Rentals.75caaa')}</h4>
      <div className="overflow-x-auto mb-6">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="text-left py-3 text-slate-500 font-medium">{t('pg.Rentals.a05362')}</th>
              <th className="text-center py-3 text-slate-500 font-medium">{t('pg.Rentals.0b062c')}</th>
              <th className="text-center py-3 text-slate-500 font-medium">{t('pg.Rentals.fff92a')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-4 text-slate-900 dark:text-white font-medium">{t('pg.Rentals.628b36')}</td>
              <td className="py-4 text-center">
                <span className="text-green-500 font-bold text-lg">1 700 kr</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.25a71a')}</span>
              </td>
              <td className="py-4 text-center">
                <span className="text-slate-600 dark:text-slate-300 font-bold text-lg">1 200 kr</span>
                <span className="text-slate-400 text-sm block">{t('pg.Rentals.25a71a')}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-slate-400 text-sm mb-6 text-center">{t('pg.Rentals.04beb8')}</p>

      {/* Additional info boxes */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Deposit */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <h5 className="text-green-500 font-bold mb-2">{t('pg.Rentals.48defb')}</h5>
          <p className="text-slate-600 text-sm">
            {t('pg.Rentals.203984')} <strong>2 000 kr</strong>{t('pg.Rentals.c18f6d')}
          </p>
        </div>

        {/* Day before */}
        <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
          <h5 className="text-slate-900 font-bold mb-2">{t('pg.Rentals.a64b82')}</h5>
          <p className="text-slate-500 text-sm">
            {t('pg.Rentals.0768d4')} <strong className="text-green-500">3 500 kr</strong> {t('pg.Rentals.ac0997')}
          </p>
        </div>
      </div>

      <div className="mt-4 bg-slate-100 rounded-lg p-4">
        <p className="text-slate-500 text-sm">
          <strong className="text-slate-900 dark:text-white">{t('pg.Rentals.3c814d')}</strong> {t('pg.Rentals.98f3a2')}
        </p>
      </div>

      <div className="mt-6 bg-green-500/20 border border-green-500/40 rounded-lg p-4 text-center">
        <p className="text-green-500 font-bold">
          {t('pg.Rentals.9411a2')}
        </p>
      </div>
    </div>
  </div>
);

export const Rentals = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('klubbhuset');

  return (
    <div data-testid="rentals-page">
      <SEO 
        title={t('pg.Rentals.274ecf')} 
        description={t('pg.Rentals.4913fc')}
      />
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
            alt={t('pg.Rentals.e73fdb')}
            loading="eager"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('pg.Rentals.274ecf')}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('pg.Rentals.7b4cce')}<br />
            <span className="text-green-400">{t('pg.Rentals.2b25b8')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {t('pg.Rentals.441806')}
          </p>
        </div>
      </section>

      {/* Location Tabs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Rentals.b4ca81')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('pg.Rentals.2d84c6')}
            </h2>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full flex justify-center gap-4 bg-transparent h-auto mb-8 flex-wrap h-auto">
              <TabsTrigger 
                value="klubbhuset" 
                data-testid="tab-klubbhuset"
                className="data-[state=active]:bg-green-500 data-[state=active]:text-black bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white px-4 sm:px-8 py-3 sm:py-4 rounded-xl font-bold uppercase tracking-wider flex-1 max-w-xs"
              >
                <div className="text-center">
                  <span className="block text-base sm:text-lg">{t('pg.Rentals.82b2c4')}</span>
                  <span className="block text-xs opacity-70 font-normal normal-case">{t('pg.Rentals.9e100a')}</span>
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="sfo" 
                data-testid="tab-sfo"
                className="data-[state=active]:bg-green-500 data-[state=active]:text-black bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white px-4 sm:px-8 py-3 sm:py-4 rounded-xl font-bold uppercase tracking-wider flex-1 max-w-xs"
              >
                <div className="text-center">
                  <span className="block text-base sm:text-lg">{t('pg.Rentals.5d6fd2')}</span>
                  <span className="block text-xs opacity-70 font-normal normal-case">{t('pg.Rentals.556afe')}</span>
                </div>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="klubbhuset">
              <KlubbhusetSection t={t} />
            </TabsContent>

            <TabsContent value="sfo">
              <SFOSection t={t} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <RentalInquiryForm />
        </div>
      </section>
    </div>
  );
};

// Rental Inquiry Form Component
const RentalInquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    venue: 'klubbhuset',
    event_type: '',
    preferred_date: '',
    guest_count: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/rentals/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          guest_count: parseInt(formData.guest_count)
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success(t('pg.Rentals.01f385'));
      } else {
        const error = await response.json().catch(() => ({}));
        const detail = Array.isArray(error.detail)
          ? error.detail.map((d) => d.msg).join(', ')
          : error.detail;
        toast.error(detail || t('pg.Rentals.3b0074'));
      }
    } catch (error) {
      toast.error(t('pg.Rentals.3b0074'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-slate-100 rounded-2xl border border-slate-200 p-8 text-center">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('pg.Rentals.444ef8')}</h2>
        <p className="text-slate-500 mb-6">
          {t('pg.Rentals.e52582')}
        </p>
        <Button
          onClick={() => setSubmitted(false)}
          variant="outline"
          className="border-slate-300 text-slate-900 hover:bg-slate-100 dark:bg-slate-700"
        >
          {t('pg.Rentals.c41b25')}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 rounded-2xl border border-slate-200 p-8">
      <h2 className="text-3xl font-bold text-slate-900 mb-2 text-center">
        {t('pg.Rentals.b1115c')}
      </h2>
      <p className="text-slate-500 mb-8 text-center">
        {t('pg.Rentals.b7c6f1')}
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">{t('pg.Rentals.a6f095')}</label>
            <Input
              type="text"
              data-testid="rental-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-white border-slate-200 text-slate-900 dark:text-white"
              placeholder={t('pg.Rentals.69f920')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">{t('pg.Rentals.64c440')}</label>
            <Input
              type="email"
              data-testid="rental-email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-white border-slate-200 text-slate-900 dark:text-white"
              placeholder="din@epost.no"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">{t('pg.Rentals.95e7b3')}</label>
          <Input
            type="tel"
            data-testid="rental-phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-white border-slate-200 text-slate-900 dark:text-white"
            placeholder="+47 123 45 678"
            required
          />
        </div>

        {/* Lokale-valg som tydelige klikkbare kort i stedet for rullgardin */}
        <div>
          <label className="block text-base font-bold text-slate-900 dark:text-white mb-1">
            {t('pg.Rentals.c7fc1a')}
          </label>
          <p className="text-sm text-slate-500 mb-3">{t('pg.Rentals.8fda2c')}</p>
          <div
            className="grid sm:grid-cols-2 gap-3"
            role="radiogroup"
            aria-label={t('pg.Rentals.fb09c9')}
            data-testid="rental-venue-group"
          >
            {[
              {
                value: 'klubbhuset',
                title: t('pg.Rentals.82b2c4'),
                subtitle: t('pg.Rentals.9e100a'),
                fromPrice: t('pg.Rentals.711fef'),
                testid: 'rental-venue-klubbhuset',
              },
              {
                value: 'sfo',
                title: t('pg.Rentals.5d6fd2'),
                subtitle: t('pg.Rentals.6b130d'),
                fromPrice: t('pg.Rentals.3e4c02'),
                testid: 'rental-venue-sfo',
              },
            ].map((option) => {
              const selected = formData.venue === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={option.testid}
                  onClick={() => setFormData({ ...formData, venue: option.value })}
                  className={`flex items-start gap-3 text-left rounded-xl border-2 px-4 py-4 transition-all ${
                    selected
                      ? 'border-green-500 bg-green-500/10 shadow-sm'
                      : 'border-slate-200 dark:border-slate-700 bg-white hover:border-green-500/50'
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                      selected
                        ? 'border-green-500 bg-green-500'
                        : 'border-slate-300 dark:border-slate-600 bg-white'
                    }`}
                    aria-hidden="true"
                  >
                    {selected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-4 w-4 text-white"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="flex-1">
                    <span className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 dark:text-white">
                        {option.title}
                      </span>
                      <span
                        className="text-xs font-bold uppercase tracking-wider text-green-600 bg-green-500/10 border border-green-500/30 rounded-full px-2 py-0.5"
                        data-testid={`${option.testid}-price`}
                      >
                        {option.fromPrice}
                      </span>
                    </span>
                    <span className="block text-sm text-slate-500 mt-1">{option.subtitle}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">{t('pg.Rentals.a3a9f4')}</label>
            <Input
              type="text"
              data-testid="rental-event-type"
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              className="bg-white border-slate-200 text-slate-900 dark:text-white"
              placeholder={t('pg.Rentals.f238be')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">{t('pg.Rentals.a8bb77')}</label>
            <Input
              type="number"
              data-testid="rental-guest-count"
              value={formData.guest_count}
              onChange={(e) => setFormData({ ...formData, guest_count: e.target.value })}
              className="bg-white border-slate-200 text-slate-900 dark:text-white"
              placeholder={t('pg.Rentals.3b60e8')}
              min="1"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">{t('pg.Rentals.558e03')}</label>
          <Input
            type="date"
            data-testid="rental-date"
            value={formData.preferred_date}
            onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
            className="bg-white border-slate-200 text-slate-900 dark:text-white"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">{t('pg.Rentals.0f03a1')}</label>
          <textarea
            data-testid="rental-message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full bg-white border border-slate-200 text-slate-900 rounded-md px-3 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder={t('pg.Rentals.47a0b0')}
          />
        </div>

        <Button
          type="submit"
          data-testid="rental-submit"
          className="w-full bg-green-500 text-black font-bold uppercase tracking-wider py-6 hover:bg-green-500/90"
          disabled={loading}
        >
          {loading ? 'Sender...' : (
            <>
              <Send className="mr-2 w-5 h-5" />
              {t('pg.Rentals.b1115c')}
            </>
          )}
        </Button>
      </form>

      {/* Contact info */}
      <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
        <p className="text-slate-500 text-sm text-center mb-4">
          {t('pg.Rentals.e9c695')}
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 text-center">
            <Mail className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <a href="mailto:utleie@veitvet-sportsklubb.no" className="text-green-500 hover:underline text-sm">
              utleie@veitvet-sportsklubb.no
            </a>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <Phone className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <a href="tel:+4790715725" className="text-green-500 hover:underline text-sm">
              +47 907 15 725
            </a>
            <p className="text-slate-400 text-xs mt-1">{t('pg.Rentals.baec5f')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rentals;
